/**
 * useConnectTokenVerification Hook
 *
 * React hook for verifying Connect tokens in customer portal.
 *
 * Usage in Customer Portal:
 * const { token, params, verified, loading, error } = useConnectTokenVerification();
 *
 * useEffect(() => {
 *   if (verified && params) {
 *     // Navigate to requested target with verified identity
 *     navigateToProfile(params.profileId, params.targetUrl);
 *   }
 * }, [verified, params]);
 */

import { useEffect, useState, useCallback } from 'react';
import { connectTokenVerifier } from '../services/connectTokenVerifier';

export interface ConnectTokenParams {
  kid: string;
  signature: string;
  issuedAtUtc: string;
  mspTenantId?: string;
  customerTenantId?: string;
  profileId?: string;
  b2bUpn?: string;
  targetUrl?: string;
}

export interface UseConnectTokenVerificationState {
  /**
   * Parsed token from URL parameters
   */
  token: ConnectTokenParams | null;

  /**
   * Extracted launch parameters
   */
  params: {
    mspTenantId?: string;
    customerTenantId?: string;
    profileId?: string;
    b2bUpn?: string;
    targetUrl?: string;
  } | null;

  /**
   * Whether the token has been successfully verified
   */
  verified: boolean;

  /**
   * Whether verification is in progress
   */
  loading: boolean;

  /**
   * Verification error, if any
   */
  error: Error | null;

  /**
   * Verification result message
   */
  message: string;
}

/**
 * Hook for verifying Connect tokens in customer portal
 *
 * @param autoVerify Whether to automatically verify token on mount (default: true)
 * @param jwksUrl Optional JWKS endpoint URL for public key retrieval
 */
export const useConnectTokenVerification = (
  autoVerify: boolean = true,
  jwksUrl?: string
): UseConnectTokenVerificationState & {
  retry: () => Promise<void>;
  clear: () => void;
} => {
  const [token, setToken] = useState<ConnectTokenParams | null>(null);
  const [params, setParams] = useState<
    | {
        mspTenantId?: string;
        customerTenantId?: string;
        profileId?: string;
        b2bUpn?: string;
        targetUrl?: string;
      }
    | null
  >(null);
  const [verified, setVerified] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [message, setMessage] = useState('');

  // Initialize verifier with JWKS URL if provided
  useEffect(() => {
    if (jwksUrl) {
      // Note: Would need to add setJwksUrl method to verifier
      // For now, JWKS URL should be passed during verifier initialization
    }
  }, [jwksUrl]);

  // Extract token from URL parameters
  const extractTokenFromUrl = useCallback((): ConnectTokenParams | null => {
    try {
      const searchParams = new URLSearchParams(window.location.search);
      const extractedToken =
        connectTokenVerifier.parseConnectTokenFromUrl(searchParams);

      if (!extractedToken) {
        return null;
      }

      return {
        kid: extractedToken.kid,
        signature: extractedToken.signature,
        issuedAtUtc: extractedToken.issuedAtUtc,
        mspTenantId: extractedToken.mspTenantId,
        customerTenantId: extractedToken.customerTenantId,
        profileId: extractedToken.profileId,
        b2bUpn: extractedToken.b2bUpn,
        targetUrl: extractedToken.targetUrl,
      };
    } catch (err) {
      console.error('Failed to extract token from URL:', err);
      return null;
    }
  }, []);

  // Verify token
  const verifyToken = useCallback(async () => {
    setLoading(true);
    setError(null);
    setMessage('Verifying Connect token...');

    try {
      const extractedToken = extractTokenFromUrl();
      if (!extractedToken) {
        setMessage('No Connect token found in URL');
        setLoading(false);
        return;
      }

      setToken(extractedToken);

      // Prepare payload for verification
      const payload = {
        mspTenantId: extractedToken.mspTenantId || '',
        customerTenantId: extractedToken.customerTenantId || '',
        b2bUpn: extractedToken.b2bUpn || '',
        profileId: extractedToken.profileId || '',
        targetUrl: extractedToken.targetUrl || '',
        issuedAtUtc: extractedToken.issuedAtUtc,
      };

      // Verify signature
      const result = await connectTokenVerifier.verifyToken(
        {
          kid: extractedToken.kid,
          signature: extractedToken.signature,
          issuedAtUtc: extractedToken.issuedAtUtc,
        },
        payload
      );

      if (result.valid) {
        setVerified(true);
        setMessage('Connect token verified successfully');
        setParams({
          mspTenantId: extractedToken.mspTenantId,
          customerTenantId: extractedToken.customerTenantId,
          profileId: extractedToken.profileId,
          b2bUpn: extractedToken.b2bUpn,
          targetUrl: extractedToken.targetUrl,
        });

        console.debug('Connect token verified', {
          kid: extractedToken.kid,
          verifiedAt: result.verifiedAt,
        });
      } else {
        setError(new Error(result.message));
        setMessage(`Token verification failed: ${result.message}`);
        console.warn('Connect token verification failed', result);
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
      setMessage(`Verification error: ${error.message}`);
      console.error('Token verification error:', error);
    } finally {
      setLoading(false);
    }
  }, [extractTokenFromUrl]);

  // Auto-verify on mount
  useEffect(() => {
    if (autoVerify) {
      verifyToken();
    }
  }, [autoVerify, verifyToken]);

  // Manual retry
  const retry = useCallback(async () => {
    await verifyToken();
  }, [verifyToken]);

  // Clear state
  const clear = useCallback(() => {
    setToken(null);
    setParams(null);
    setVerified(false);
    setError(null);
    setMessage('');
  }, []);

  return {
    token,
    params,
    verified,
    loading,
    error,
    message,
    retry,
    clear,
  };
};

export default useConnectTokenVerification;
